<template>
  <teleport v-if="isMounted" to="#tools">
    <div class="flex items-center gap-5 px-4 border-b border-gray-200 h-11">

      <button type="button" class="inline-flex items-center p-1 text-gray-700 border border-transparent rounded-full hover:bg-gray-50 focus:outline-none" @click="reload">
        <RefreshIcon :class="[loading ? 'animate-reverse-spin' : '', 'h-5 w-5']" aria-hidden="true" />
      </button>
      <template v-if="fullSelection">
        <button type="button" @click="batchEdit('owner')" v-if="canChangeTenant" class="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          {{ $t("device.action.owner") }}
        </button>
        <Menu as="div" class="relative hidden text-left md:inline-block">


          <transition enter-active-class="transition duration-100 ease-out" enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in" leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
            <MenuItems class="absolute left-0 w-56 mt-2 origin-top-left bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div class="py-1">
                <MenuItem v-slot="{ active }">
                  <button @click="batchEdit('alarm')" :class="[
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                    'block px-4 py-2 text-sm w-full text-left',
                  ]">
                    {{ $t("device.action.alarmConfig") }}
                  </button>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
        <button type="button" @click="showApplyConfigTemplate = true" v-if="isSuperAdmin" class="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          {{ $t("device.action.applyConfigTemplate") }}
        </button>
      </template>
    </div>
  </teleport>

  <TransitionRoot as="template" :show="showSlideIn">
    <Dialog as="div" static class="fixed inset-0 z-20 overflow-hidden" @close="showSlideIn = false" :open="showSlideIn">
      <div class="absolute inset-0 overflow-hidden">
        <div class="fixed inset-y-0 right-0 flex max-w-full pl-10">
          <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700" enter-from="translate-x-full" enter-to="translate-x-0" leave="transform transition ease-in-out duration-500 sm:duration-700" leave-from="translate-x-0" leave-to="translate-x-full">
            <div class="w-screen max-w-lg">
              <div class="flex flex-col h-full overflow-y-scroll bg-white divide-y divide-gray-200 shadow-xl">
                <component :is="slideInBatchEditor" @close="showSlideIn = false" :deviceSelection="deviceSelection" />
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <div class="h-full overflow-visible overflow-y-scroll" ref="devicesContainer">
    <table class="hidden w-full md:table">
      <thead class="bg-gray-50">
        <tr>
          <th v-if="isSuperAdmin || canChangeTenant" scope="col" class="relative w-12 px-4">
            <input type="checkbox" v-model="fullSelection" :class="{ partial: partialSelection }" class="text-blue-500 rounded" />
          </th>

          <th scope="col" class="w-5 px-4 py-2">
            <div class="text-sm font-semibold text-left text-gray-900 group">
              <InformationCircleIcon class="w-4 text-gray-500 group hover:text-cyan-800" aria-hidden="true" />
              <div class="absolute invisible px-3 py-3 mt-2 text-sm font-semibold text-left text-gray-900 truncate origin-top bg-white divide-y divide-gray-100 rounded-md shadow-lg group-hover:visible ring-1 ring-black ring-opacity-5 focus:outline-none">
                {{ $t("device.header.deviceConnectivityStatus") }}
              </div>
            </div>
          </th>

          <th scope="col" class="px-1 py-3.5 text-left text-sm font-semibold text-gray-900">
            {{ $t("device.header.serialNumber") }}
          </th>

          <th scope="col" class="px-1 py-3.5 truncate text-left text-sm font-semibold text-gray-900">
            {{ $t("device.header.tenant") }}
          </th>

          <th scope="col" class="px-1 py-3.5 truncate text-left text-sm font-semibold text-gray-900">
            {{ $t("device.header.id") }}
          </th>

          <th scope="col" class="px-1 py-3.5 truncate text-left text-sm font-semibold text-gray-900">
            {{ $t("device.header.braceletColumn") }}
          </th>

          <th v-if="isSuperAdmin" scope="col" class="px-1 py-3.5 truncate text-left text-sm font-semibold text-gray-900">
            {{ $t("device.header.arcConfig") }}
          </th>

          <th scope="col" class="py-3.5 text-center text-sm font-semibold text-gray-900">
          </th>

          <th scope="col" class="py-3.5 w-20 text-left text-sm font-semibold text-gray-900">
            {{ $t("device.header.version") }}
          </th>
        </tr>
      </thead>

      <tbody class="bg-white divide-y divide-gray-200">
        <tr v-for="device in devices" :key="device.id" class="cursor-pointer hover:bg-gray-100 hover:shadow-list">
          <td v-if="isSuperAdmin || canChangeTenant" data-interactive class="w-12 px-4 py-2 ">
            <input v-if="hasDeviceAdmin(device) && canChangeTenant" type="checkbox" class="block text-blue-500 rounded" :value="device.id" v-model="deviceSelection" />
          </td>
          <DeviceListItem :device="device" :isSuperAdmin="isSuperAdmin" />
        </tr>

        <DeviceListPlaceholder v-if="devicesLoading" :isSuperAdmin="isSuperAdmin" />
      </tbody>
    </table>

    <div class="block px-4 py-2 md:hidden">
      <div v-for="device in devices" :key="device.id" class="mb-4">
        <DeviceCard :device="device" :isSuperAdmin="isSuperAdmin" :hasDeviceAdmin="hasDeviceAdmin(device)" :deviceSelection="deviceSelection" @update:deviceSelection="deviceSelection = $event" />
      </div>

      <div v-if="devicesLoading" class="space-y-4">
        <div v-for="i in 3" :key="i" class="p-4 bg-white border border-gray-200 rounded-lg animate-pulse">
          <div class="w-3/4 h-4 mb-3 bg-gray-200 rounded"></div>
          <div class="w-1/2 h-3 mb-2 bg-gray-200 rounded"></div>
          <div class="w-2/3 h-3 mb-2 bg-gray-200 rounded"></div>
          <div class="w-1/3 h-3 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  </div>


  <SimpleAlert @cancel="alertOptions.showAlert = false" @approve="alertOptions.approve(); alertOptions.showAlert = false;" :open="alertOptions.showAlert" :title="alertOptions.title" :description="alertOptions.description" action="Apply">
  </SimpleAlert>
  <ApplyBatchConfigTemplate v-if="isSuperAdmin" :open="showApplyConfigTemplate" @cancel="showApplyConfigTemplate = false" :devices="deviceSelection"></ApplyBatchConfigTemplate>

  <teleport to="body">
    <div v-if="selectedCount && !(showSlideIn && isMobile)" class="fixed z-40 transform -translate-x-1/2 md:block bottom-4 left-1/2 ">
      <div role="status" class="flex items-center gap-2 px-4 py-2 text-white rounded-full shadow-lg bg-blue-500/80">
        <span class="text-xs font-medium">
          {{ selectedCount == 1 ? $t('device.selectionSingular', { selection: selectedCount }) : $t('device.selection', { selection: selectedCount }) }}
        </span>
      </div>
    </div>
  </teleport>
</template>

<script>
import DeviceListItem from "@/components/DeviceListItem.vue";
import DeviceCard from "@/components/DeviceCard.vue";
import { computed, onMounted, ref, shallowRef, watch } from "vue";
import { useStore } from "vuex";
import { RefreshIcon, InformationCircleIcon } from "@heroicons/vue/outline";
import { DotsVerticalIcon } from "@heroicons/vue/solid";
import { Dialog, Menu, MenuButton, MenuItem, MenuItems, TransitionChild, TransitionRoot } from "@headlessui/vue";
import DeviceListPlaceholder from "@/components/DeviceListPlaceholder";
import OwnerBatch from "@/components/OwnerBatch";
import SimpleAlert from "@/components/modals/SimpleAlert";
import ApplyBatchConfigTemplate from "@/components/modals/ApplyBatchConfigTemplate.vue";
import { useInfiniteScroll } from '@vueuse/core'
import { USER_PERMISSIONS } from "@/lib/userHasPermission";
import { useDebounceFn } from '@vueuse/core'

const batchEditors = {
  owner: OwnerBatch,
};

export default {
  components: {
    DeviceListPlaceholder,
    DeviceListItem,
    DeviceCard,
    DotsVerticalIcon,
    RefreshIcon,
    InformationCircleIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Dialog,
    TransitionChild,
    TransitionRoot,
    SimpleAlert,
    ApplyBatchConfigTemplate
  },

  setup() {
    const showSlideIn = ref(false);
    const slideInBatchEditor = shallowRef(null);
    const store = useStore();
    const deviceSelection = ref([]);
    const isMounted = ref(false);
    const devices = computed(() => Array.from(store.state.device.all.values()));
    const loading = computed(() => store.state.device.loading || store.state.device.loadingDevices || store.state.device.loadingAggregations || store.state.loadingTenants);
    const devicesLoading = computed(() => store.state.device.loadingDevices);
    const showApplyConfigTemplate = ref(false);
    const devicesContainer = ref(null)
    const tenants = computed(() => store.state.allTenants || []);
    const isAdminCached = ref(null);

    const userRoles = computed(() => store.getters["auth/userRoles"]);
    const isSuperAdmin = computed(() => store.getters["auth/isSuperAdmin"]);

    const hasDeviceAdmin = (device) => {
      if (isSuperAdmin.value) return true;
      const tenantSlug = device.owner?.slug;
      if (!tenantSlug) return false;
      const requiredPermission = `${tenantSlug}:${USER_PERMISSIONS.ADMIN}`;
      return userRoles.value.includes(requiredPermission);
    }

    const isAdmin = computed(() => {

      if (isAdminCached.value !== null) {
        return isAdminCached.value;
      }

      if (!loading.value && !devicesLoading.value && devices.value.length > 0) {

        if (store.getters["auth/isSuperAdmin"]) {
          return true;
        }

        const userRoles = store.getters["auth/userRoles"];
        return devices.value.some(device => {
          const tenantSlug = device.owner?.slug;
          if (!tenantSlug) return false;
          const requiredPermission = `${tenantSlug}:${USER_PERMISSIONS.ADMIN}`;
          return userRoles.includes(requiredPermission);
        });
      }
      return false;
    });

    watch([loading, devicesLoading, devices], () => {
      if (isAdminCached.value === null && !loading.value && !devicesLoading.value && devices.value.length > 0) {
        isAdminCached.value = isAdmin.value;
      }
    }, { immediate: true });

    const canChangeTenant = computed(() => isAdmin.value && tenants.value.length > 1);

    useInfiniteScroll(devicesContainer,
      async () => {
        if (!devicesLoading.value && store.state.device.hasNextPage) {
          await store.dispatch("device/getDevices");
        }
      },
      {
        distance: 500,
        interval: 100,
        canLoadMore: () => {
          return store.state.device.hasNextPage && !devicesLoading.value
        },
      })

    const partialSelection = computed(() => {
      return deviceSelection.value.length > 0 && devices.value.length !== deviceSelection.value.length;
    });

    const alertOptions = ref({
      showAlert: false,
      title: "",
      description: "",
      approve: () => {
      },
    });

    const fullSelection = computed({
      get: () => deviceSelection.value.length > 0,
      set: (checked) => {
        if (checked) {
          if (isSuperAdmin.value) {
            deviceSelection.value = devices.value.map((device) => device.id);
          } else {
            deviceSelection.value = devices.value.filter(device => hasDeviceAdmin(device)).map(device => device.id);
          }
        } else {
          deviceSelection.value = [];
        }
      },
    });

    const batchEdit = (editor) => {
      slideInBatchEditor.value = batchEditors[editor];
      showSlideIn.value = true;
    };

    onMounted(async () => {
      isMounted.value = true;


      store.dispatch("device/getDevices", true);
      store.dispatch("device/getAggregations");


    });

    watch(showSlideIn, (newVal, oldVal) => {
      if (oldVal && !newVal) {
        clearSelection();
      }
    });

    const reload = useDebounceFn(async () => {
      clearSelection();

      store.dispatch("device/getAggregations", { refresh: true });
      store.dispatch("device/getDevices", true);

    }, 300);

    const selectedCount = computed(() => deviceSelection.value.length);

    const clearSelection = () => {
      deviceSelection.value = [];
    };

    const isMobile = computed(() => {
      if (typeof window !== 'undefined') {
        return window.innerWidth < 640;
      }
      return false;
    });

    return {
      isMounted,
      alertOptions,
      showSlideIn,
      slideInBatchEditor,
      batchEdit,
      devices,
      devicesLoading,
      loading,
      deviceSelection,
      partialSelection,
      fullSelection,
      isSuperAdmin,
      showApplyConfigTemplate,
      devicesContainer,
      canChangeTenant,
      hasDeviceAdmin,
      reload,
      selectedCount,
      isMobile
    };
  },


};
</script>

<style lang="scss">
[type="checkbox"].partial:checked {
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m 4.5,7 h 7 c 0.554,0 1,0.446 1,1 0,0.554 -0.446,1 -1,1 h -7 c -0.554,0 -1,-0.446 -1,-1 0,-0.554 0.446,-1 1,-1 z'/%3e%3c/svg%3e");
}

// Add smooth scrolling behavior
.overflow-y-scroll {
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch; // For iOS
}

// Optimize rendering performance
tbody tr {
  will-change: transform; // Hint to browser for optimization
}
</style>
